import React, { CSSProperties, ReactNode, Ref } from 'react';
import { RefSelectProps, Select } from 'antd';
import styled from 'styled-components';

const { OptGroup, Option } = Select;

type ValueType = number | string | number[] | string[];

type Item = {
  id: number | string;
  label?: string;
  list: Array<{ id: number | string; name: string }>;
};

/**
 * data is an array because sometimes we need to show the select options by group
 * Clarification: label property is defined to be the title of the group
 * Use case 1: If it's not a group then don't add a label
 * Use case 2: if it's only a one list then the data will contain only one element
 */

export type Props = {
  open?: boolean;
  mode?: 'multiple' | 'tags';
  defaultValue?: ValueType;
  selectRef?: Ref<RefSelectProps>;
  value?: ValueType;
  data: Item[];
  style?: CSSProperties;
  optionStyle?: CSSProperties;
  showSearch?: boolean;
  allowClear?: boolean;
  dropdownStyle?: CSSProperties;
  popupMatchSelectWidth?: boolean;
  disabled?: boolean;
  className?: string;
  suffixIcon?: ReactNode;
  hasMonospaceFont?: boolean;
  /**
   * When true, the value passed to onChange will always be an array,
   * even when only a single item is selected
   */
  valueAsArray?: boolean;
  onDropdownVisibleChange?: (open: boolean) => void;
  onChange: (value: ValueType) => void;
  filterOption?: (input: string, option?: { value: string }) => boolean;
};

export const MonospaceOptionLabel = styled.span`
  font-family: 'Courier New', Courier, monospace !important;
  font-size: 14px;
  font-weight: 600;
`;

const InputSelect = ({
  open,
  mode,
  defaultValue,
  data,
  style,
  optionStyle,
  showSearch,
  selectRef,
  value,
  allowClear = true,
  dropdownStyle,
  popupMatchSelectWidth,
  disabled,
  className,
  suffixIcon,
  hasMonospaceFont,
  valueAsArray,
  onDropdownVisibleChange,
  onChange,
  filterOption,
}: Props) => {
  if (!Array.isArray(data)) {
    throw new Error('data should be an array');
  }

  const handleChange = (selectedValue: ValueType) => {
    if (valueAsArray && selectedValue !== undefined && !Array.isArray(selectedValue)) {
      if (typeof selectedValue === 'string') {
        onChange([selectedValue] as string[]);
      } else if (typeof selectedValue === 'number') {
        onChange([selectedValue] as number[]);
      }
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <Select
      open={open}
      onDropdownVisibleChange={onDropdownVisibleChange}
      placement="bottomLeft"
      mode={mode}
      disabled={disabled}
      optionFilterProp="children"
      onChange={handleChange}
      defaultValue={defaultValue}
      ref={selectRef}
      value={value}
      style={style}
      showSearch={showSearch}
      allowClear={allowClear}
      dropdownStyle={dropdownStyle}
      popupMatchSelectWidth={popupMatchSelectWidth}
      className={className}
      suffixIcon={suffixIcon}
      filterOption={filterOption}
    >
      {data.map((elem) =>
        elem?.label ? (
          <OptGroup label={elem.label} key={elem.id}>
            {elem.list.map((item) => {
              return (
                <Option key={item.id} value={item.id} style={optionStyle}>
                  {hasMonospaceFont && item.id !== value ? (
                    <MonospaceOptionLabel>{item.name}</MonospaceOptionLabel>
                  ) : (
                    item.name
                  )}
                </Option>
              );
            })}
          </OptGroup>
        ) : (
          elem.list.map((item) => {
            return (
              <Option key={item.id} value={item.id} style={optionStyle}>
                {hasMonospaceFont && item.id !== value ? (
                  <MonospaceOptionLabel>{item.name}</MonospaceOptionLabel>
                ) : (
                  item.name
                )}
              </Option>
            );
          })
        )
      )}
    </Select>
  );
};

export default InputSelect;
